import { useState } from 'react'
import { TIER_STYLES, type LeadEnrichment } from '@/types/crm'
import { formatDate } from '@/shared/util/dates'

const CONFIDENCE_NOTE: Record<string, string> = {
  high: 'all three axes have a sourced signal',
  medium: 'two of three axes are sourced',
  low: 'one axis or fewer is sourced — read this before acting on it',
}

/**
 * The audit trail behind the score.
 *
 * The snapshot and the angle live at the top of the drawer where they are
 * read; what is left here is the working: the three axes, the harvested facts,
 * and the page every one of them came from.
 *
 * The sources are the point. A score a rep cannot audit is a score they will
 * stop trusting the first time it is wrong, so the evidence list is one click
 * away rather than buried — and a field with nothing behind it renders as an
 * em-dash, never as a plausible-looking default.
 */
export function EnrichmentCard({ enrichment }: { enrichment: LeadEnrichment }) {
  const [showEvidence, setShowEvidence] = useState(false)
  const e = enrichment

  if (e.leadScore === null && !e.snapshot) {
    return (
      <section className="card enrich">
        <div className="card-label">Harvested profile</div>
        <div className="empty-note">
          Not harvested yet. The enrichment job fills this in — score, team size,
          tech stack and the outreach hook, each with the page it came from.
        </div>
      </section>
    )
  }

  const tier = e.tier ? TIER_STYLES[e.tier] : null
  const axes: [string, number | null][] = [
    ['Size', e.axisSize],
    ['Digital', e.axisDigital],
    ['Power', e.axisPower],
  ]

  return (
    <section className="card enrich">
      <div className="enrich__head">
        <div className="card-label">Harvested profile</div>
        <div className="enrich__headright">
          {e.leadScore !== null && tier && (
            <span className="pill" style={{ background: tier.bg, color: tier.color }}>
              {e.leadScore} · tier {e.tier}
            </span>
          )}
          {e.estAcvEur !== null && (
            <span className="enrich__acv">€{e.estAcvEur.toLocaleString('nb-NO')} est. ACV</span>
          )}
        </div>
      </div>

      <div className="enrich__axes">
        {axes.map(([label, v]) => (
          <div className="enrich__axis" key={label}>
            <div className="enrich__axislabel">{label}</div>
            <div className="enrich__axisvalue">{v === null ? '—' : `${v}/10`}</div>
          </div>
        ))}
      </div>

      <dl className="enrich__facts">
        {/*
          Team size stays, and it is the only harvested figure the tool now
          depends on: `seatsExpected` was seeded from it on 4 Sep 2026, so this
          line with its source is how anybody can see where a seat count came
          from on a lead nobody has typed one onto. Delete the source of a
          number the moment you copy it and the number becomes unexplainable.
        */}
        <Fact label="Team size" value={e.teamSize === null ? null : String(e.teamSize)} note={e.teamSizeSource} />
        <Fact label="Entity" value={e.entityType} />
        <Fact label="Certification" value={e.certLevel} />
        <Fact label="CMS" value={e.cms} />
        <Fact label="Map tools" value={e.mapTools.join(', ')} />
        <Fact label="Safety tools" value={e.safetyTools.join(', ')} />
        <Fact
          label="Price / person / day"
          value={e.pricePerPersonDayEur === null ? null : `€${e.pricePerPersonDayEur}`}
        />
        {/* Booking and Best contact window came off with their overview facts on
            4 Sep 2026 — not used, on either surface. The harvester still writes
            `bookingVendor`, `bookingMode` and `contactWindow`, so nothing is
            lost if they are ever wanted back. */}
      </dl>

      {(e.stackComplementary || e.displacementRisk) && (
        <div className="enrich__angle">
          {e.stackComplementary && (
            <span className="enrich__tag enrich__tag--good">
              Booking stack is complementary — Skida covers planning and safety, which it does not
            </span>
          )}
          {e.displacementRisk && (
            <span className="enrich__tag enrich__tag--warn">
              Runs its own system — harder to displace, pitch alongside rather than instead
            </span>
          )}
        </div>
      )}

      <div className="enrich__foot">
        {e.confidence && (
          <span className={`enrich__conf enrich__conf--${e.confidence}`}>
            {e.confidence} confidence — {CONFIDENCE_NOTE[e.confidence]}
          </span>
        )}
        {e.verify && (
          <span className="enrich__conf enrich__conf--verify">
            Contains an inferred value — verify before outreach
          </span>
        )}
        {e.siteBlocked && (
          <span className="enrich__conf enrich__conf--verify">
            Their site refused to load; scored from registries and social only
          </span>
        )}
      </div>

      {e.evidence.length > 0 && (
        <>
          <button className="btn-mini enrich__evidencebtn" onClick={() => setShowEvidence((v) => !v)}>
            {showEvidence ? 'Hide sources' : `Sources (${e.evidence.length})`}
          </button>
          {showEvidence && (
            <ul className="enrich__evidence">
              {e.evidence.map((ev, i) => (
                <li key={`${ev.field}-${i}`}>
                  <code>{ev.field}</code>{' '}
                  {ev.url.startsWith('http') ? (
                    <a href={ev.url} target="_blank" rel="noreferrer">
                      {ev.url}
                    </a>
                  ) : (
                    <span className="muted">{ev.url}</span>
                  )}{' '}
                  <span className="micro">{ev.retrievedAt}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {e.enrichedAt && (
        <div className="micro enrich__when">
          Harvested {formatDate(e.enrichedAt)}
          {e.source ? ` · ${e.source}` : ''}
        </div>
      )}
    </section>
  )
}

function Fact({
  label,
  value,
  note,
}: {
  label: string
  value: string | null | undefined
  note?: string
}) {
  if (!value) return null
  return (
    <div className="enrich__fact">
      <dt>{label}</dt>
      <dd>
        {value}
        {note && <span className="enrich__factnote">{note}</span>}
      </dd>
    </div>
  )
}
